import { selectMerchant } from '@/entities/merchant'
import { Roles } from '@/entities/roles'
import { selectTrader } from '@/entities/trader'
import { USER_ROLE_KEY } from '@/features/auth'
import { BaseButton } from '@/shared/components/BaseButton/BaseButton'
import { BaseInput } from '@/shared/components/BaseInput/BaseInput'
import { EMAIL_REG } from '@/shared/constants/constants'
import { formatDate } from '@/shared/lib/date'
import { getStorageItem } from '@/shared/lib/storage/localStorage'
import { useAppSelector } from '@/store/hooks'
import { useForm } from 'react-hook-form'
import { useApiPut } from '@/api/main/query'
import { profileAPI } from '@/widgets/profile/api/endpoints'
import { useToast } from '@/shared/hooks'
import { BaseProfileCard } from '@/features/profile/ui'

type FormData = {
  accountDate: string
  profileId: string
  teamName: string
  email: string
  phoneNumber: string
  merchantName: string
  merchantUrl: string
  callbackUrl: string
  successUrl: string
  failedUrl: string
}

export const PrivateInfo = () => {
  const trader = useAppSelector(selectTrader)
  const merchant = useAppSelector(selectMerchant)
  const userRole = getStorageItem<Roles | null>(USER_ROLE_KEY, null)

  const { addToast } = useToast()

  const createAt = formatDate(trader?.createdAt ?? merchant?.createdAt ?? '')

  // Проверяем, есть ли необходимые данные для отображения компонента
  const hasTraderData = trader && Object.keys(trader).length > 0
  const hasMerchantData = merchant && Object.keys(merchant).length > 0

  // Определяем, нужно ли показывать компонент
  const shouldShowComponent = () => {
    switch (userRole) {
      case Roles.Trader:
        return hasTraderData
      case Roles.Merchant:
        return hasMerchantData
      default:
        return false
    }
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      accountDate: createAt,
      profileId: trader?.id ?? merchant?.id ?? '',
      teamName: trader?.teamName ?? '',
      email: trader?.email ?? '',
    },
  })

  const { mutate: updateUser, isPending } = useApiPut(profileAPI.updateUser, {
    onSuccess: () => addToast('Успешно', 'Данные обновлены', 'success'),
    onError: (e) => addToast('Ошибка', e.message, 'error'),
  })

  const onSubmit = (data: FormData) => {
    updateUser({
      teamName: data.teamName,
      email: data.email,
    })
  }

  // Если нет данных для отображения, скрываем компонент
  if (!shouldShowComponent()) {
    return null
  }

  const renderTraderFields = () => (
    <>
      <div className="flex flex-col gap-4 md:gap-6 w-full">
        <BaseInput disabled={true} label="Дата создания аккаунта" {...register('accountDate')} />
        <BaseInput disabled={true} label="ID профиля" {...register('profileId')} />
      </div>
      <div className="flex md:flex-row flex-col justify-between self-stretch gap-4">
        <BaseInput
          label="Никнейм"
          disabled={true}
          readOnly={true}
          {...register('teamName')}
          className="flex-1"
        />
        <BaseInput
          type="email"
          label="Электронная почта"
          disabled={true}
          readOnly={true}
          {...register('email', {
            pattern: {
              value: EMAIL_REG,
              message: 'Некорректный email адрес',
            },
          })}
          error={errors.email?.message}
          className="flex-1"
        />
      </div>
    </>
  )

  const renderMerchantFields = () => (
    <>
      <div className="flex flex-col gap-4 md:gap-6 w-full">
        <BaseInput disabled={true} label="Дата создания аккаунта" {...register('accountDate')} />
        <BaseInput disabled={true} label="ID мерчанта" {...register('profileId')} />
      </div>
      <div className="flex md:flex-row flex-col justify-between self-stretch gap-4">
        <BaseInput
          disabled={true}
          value={merchant?.name ?? ''}
          label="Название мерчанта"
          {...register('merchantName')}
          className="flex-1"
        />
        <BaseInput
          disabled={true}
          value={merchant?.url ?? ''}
          label="URL мерчанта"
          {...register('merchantUrl')}
          className="flex-1"
        />
      </div>
      <div className="flex flex-col justify-between self-stretch gap-4">
        <BaseInput
          disabled={true}
          value={merchant?.callbackUrl ?? ''}
          label="Callback URL"
          {...register('callbackUrl')}
          className="flex-1"
        />
        <BaseInput
          disabled={true}
          value={merchant?.successUrl ?? ''}
          label="Success URL"
          {...register('successUrl')}
          className="flex-1"
        />
        <BaseInput
          disabled={true}
          value={merchant?.failedUrl ?? ''}
          label="Failed URL"
          {...register('failedUrl')}
          className="flex-1"
        />
      </div>
    </>
  )

  return (
    <BaseProfileCard title="Личная информация">
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col items-start gap-6">
        {/* Поля для трейдера */}
        {(userRole === Roles.Trader || userRole === Roles.Admin) && hasTraderData && (
          <>{renderTraderFields()}</>
        )}

        {/* Поля для мерчанта */}
        {(userRole === Roles.Merchant || userRole === Roles.Admin) && hasMerchantData && (
          <>{renderMerchantFields()}</>
        )}

        {userRole === Roles.Merchant && (
          <BaseButton type="submit" disabled={isPending}>
            Сохранить
          </BaseButton>
        )}
      </form>
    </BaseProfileCard>
  )
}
